/**
 * Helpers for organization logos: initials for organizations without one,
 * upload checks, and a shared cache of loaded logo images.
 * See docs/frontend/components/organization-logo.md.
 */

const SKIPPED_WORDS = new Set(['the', 'of', 'and', '&']);

/** Up to two initials: "Avonworth SD" -> "AS", "The Ellis School" -> "ES" */
export const organizationInitials = (name: string | null | undefined): string => {
  const words = (name || '')
    .split(/[\s\-–/]+/)
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter((word) => word && !SKIPPED_WORDS.has(word.toLowerCase()));
  return words.slice(0, 2).map((word) => word[0].toUpperCase()).join('') || '?';
};

export const LOGO_MAX_BYTES = 5 * 1024 * 1024;
export const LOGO_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';

/** Why a chosen file can't be uploaded, or null when it looks fine (the server checks again) */
export const logoFileProblem = (file: Pick<File, 'size' | 'type'>): string | null => {
  if (!LOGO_ACCEPT.split(',').includes(file.type)) {
    return 'Choose a PNG, JPEG, GIF, or WebP image.';
  }
  if (file.size > LOGO_MAX_BYTES) {
    return 'That image is larger than 5 MB. Try a smaller version.';
  }
  return null;
};

const loaded = new Map<string, string | null>();
const pending = new Map<string, Promise<string | null>>();

/** The object URL already loaded for this logo version, if any */
export const cachedLogoUrl = (leaId: number, version: string): string | null | undefined =>
  loaded.get(`${leaId}:${version}`);

/**
 * Load a logo once per version and share it across the app. Resolves to
 * an object URL, or null if it couldn't be loaded (the caller shows initials).
 */
export const loadLogoUrl = (
  leaId: number,
  version: string,
  fetchBlob: (leaId: number, version: string) => Promise<Blob>,
): Promise<string | null> => {
  const key = `${leaId}:${version}`;
  if (loaded.has(key)) return Promise.resolve(loaded.get(key) ?? null);
  let request = pending.get(key);
  if (!request) {
    request = fetchBlob(leaId, version)
      .then((blob) => URL.createObjectURL(blob))
      .catch((err) => {
        console.warn('[OrganizationLogo] Could not load logo', { leaId, version, err });
        return null;
      })
      .then((url) => {
        loaded.set(key, url);
        pending.delete(key);
        return url;
      });
    pending.set(key, request);
  }
  return request;
};
